import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';

// Importar los dashboards específicos
import BeautyProfessionalDashboard from './BeautyProfessionalDashboard';
import HealthProfessionalDashboard from './HealthProfessionalDashboard';
import ProfessionalDashboard from './ProfessionalDashboard';

// Interface para el usuario
interface User {
  id: number;
  email: string;
  nombre: string;
  apellido: string;
  es_profesional: boolean;
  professional_type?: 'salud' | 'belleza';
  especialidad?: string;
  auth_method: string;
  picture?: string;
}

// Componente de carga mientras se determina el tipo
const DashboardLoader: React.FC = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="relative">
        {/* Spinner principal */}
        <LoadingSpinner size={128} thickness={6} />

        {/* Iconos rotativos */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-pulse">
            <div className="flex space-x-2">
              <span className="text-2xl">🏥</span>
              <span className="text-2xl">💅</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Cargando tu dashboard...</h3>
        <p className="text-gray-600">Preparando tu experiencia personalizada</p>
      </div>
      
      {/* Indicadores de progreso */}
      <div className="mt-4 flex justify-center space-x-2">
        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
        <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce delay-100"></div>
        <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce delay-200"></div>
      </div>
    </div>
  </div>
);

// Componente de error para casos no válidos
const DashboardError: React.FC<{ 
  error: string; 
  onRetry: () => void; 
  onGoHome: () => void;
}> = ({ error, onRetry, onGoHome }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L5.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      
      <h2 className="text-xl font-bold text-gray-900 mb-3">Error de acceso</h2>
      <p className="text-gray-600 mb-6">{error}</p>
      
      <div className="space-y-3">
        <button
          onClick={onRetry}
          className="w-full bg-blue-500 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-600 transition-colors"
        >
          Intentar nuevamente
        </button>
        <button
          onClick={onGoHome}
          className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
        >
          Ir al inicio
        </button>
      </div>
    </div>
  </div>
);

// Router principal para dashboards profesionales
const ProfessionalDashboardRouter: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Función para verificar y determinar el tipo de usuario
  const determineUserType = () => {
    try {
      const userData = localStorage.getItem('user');
      const loginStatus = localStorage.getItem('isLoggedIn');
      
      console.log('🔍 Verificando datos del usuario...');
      console.log('UserData exists:', !!userData);
      console.log('Login status:', loginStatus);
      
      if (!userData || loginStatus !== 'true') {
        console.log('❌ Usuario no autenticado');
        setError('No estás autenticado. Por favor inicia sesión.');
        setLoading(false);
        return;
      }

      const parsedUser: User = JSON.parse(userData);
      console.log('👤 Datos del usuario:', {
        nombre: parsedUser.nombre,
        es_profesional: parsedUser.es_profesional,
        professional_type: parsedUser.professional_type,
        especialidad: parsedUser.especialidad
      });

      // Verificar que sea profesional
      if (!parsedUser.es_profesional) {
        console.log('❌ Usuario no es profesional');
        setError('Esta sección es solo para profesionales.');
        setLoading(false);
        return;
      }

      // Verificar si tiene tipo de profesional definido
      if (!parsedUser.professional_type) {
        console.log('⚠️ Tipo de profesional no definido, usando dashboard genérico');
        // Si no tiene tipo definido, usar dashboard genérico
        setUser(parsedUser);
        setLoading(false);
        return;
      }

      // Usuario válido con tipo definido
      console.log(`✅ Usuario profesional de ${parsedUser.professional_type} verificado`);
      setUser(parsedUser);
      setLoading(false);

    } catch (error) {
      console.error('❌ Error procesando datos del usuario:', error);
      setError('Error al procesar los datos del usuario. Por favor inicia sesión nuevamente.');
      setLoading(false);
    }
  };

  // Verificar usuario al cargar el componente
  useEffect(() => {
    console.log('🚀 Iniciando ProfessionalDashboardRouter...');
    determineUserType();

    // Escuchar cambios en localStorage (para multi-tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user' || e.key === 'isLoggedIn') {
        console.log('🔄 Cambio detectado en localStorage, re-verificando usuario...');
        determineUserType();
      }
    };

    // Escuchar eventos de logout
    const handleLogout = () => {
      console.log('🔄 Logout detectado, redirigiendo...');
      setUser(null);
      setError('Sesión cerrada.');
      navigate('/');
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userLogout', handleLogout);

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLogout', handleLogout);
    };
  }, [navigate]);

  // Funciones para manejar errores
  const handleRetry = () => {
    console.log('🔄 Reintentando...');
    setError(null);
    setLoading(true);
    setTimeout(() => {
      determineUserType();
    }, 500);
  };

  const handleGoHome = () => {
    console.log('🏠 Redirigiendo al inicio...');
    navigate('/');
  };

  const handleGoLogin = () => {
    console.log('🔑 Redirigiendo al login...');
    navigate('/login');
  };

  // Estados de carga y error
  if (loading) {
    return <DashboardLoader />;
  }

  if (error) {
    return (
      <DashboardError 
        error={error} 
        onRetry={handleRetry} 
        onGoHome={error.includes('autenticado') ? handleGoLogin : handleGoHome}
      />
    );
  }

  if (!user) {
    return (
      <DashboardError 
        error="No se pudieron cargar los datos del usuario." 
        onRetry={handleRetry} 
        onGoHome={handleGoHome}
      />
    );
  }

  // Renderizar el dashboard correspondiente según el tipo de profesional
  console.log(`🎯 Renderizando dashboard para: ${user.professional_type || 'genérico'}`);

  switch (user.professional_type) {
    case 'belleza':
      console.log('💅 Cargando dashboard de belleza...');
      return <BeautyProfessionalDashboard />;
      
    case 'salud':
      console.log('🏥 Cargando dashboard de salud...');
      return <HealthProfessionalDashboard />;
      
    default:
      console.log('📊 Cargando dashboard genérico...');
      return <ProfessionalDashboard />;
  }
};

// Componente de configuración inicial para nuevos usuarios
const ProfessionalSetup: React.FC<{ user: User; onComplete: () => void }> = ({ user, onComplete }) => {
  const [selectedType, setSelectedType] = useState<'salud' | 'belleza' | null>(null);
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [saving, setSaving] = useState(false);

  const healthSpecialties = [
    'Medicina General', 'Cardiología', 'Pediatría', 'Dermatología',
    'Ginecología', 'Traumatología', 'Neurología', 'Oftalmología',
    'Psiquiatría', 'Psicología', 'Odontología', 'Kinesiología',
    'Nutrición', 'Fonoaudiología'
  ];

  const beautySpecialties = [
    'Lashista (Pestañas y Cejas)', 'Manicura/Nail Art', 'Peluquería',
    'Colorista', 'Barbería', 'Estética Facial', 'Masajes',
    'Micropigmentación', 'Lifting de Pestañas', 'Depilación',
    'Maquillaje Profesional', 'Extensiones de Cabello'
  ];

  const handleSave = async () => {
    if (!selectedType || !selectedSpecialty) return;
    
    setSaving(true);
    try {
      // Actualizar datos del usuario
      const updatedUser = {
        ...user,
        professional_type: selectedType,
        especialidad: selectedSpecialty
      };
      
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Simular llamada al backend
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      console.log('✅ Configuración inicial completada:', {
        type: selectedType,
        specialty: selectedSpecialty
      });
      
      onComplete();
    } catch (error) {
      console.error('Error guardando configuración:', error);
      alert('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl text-white">⚙️</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            ¡Bienvenido/a {user.nombre}!
          </h2>
          <p className="text-gray-600">
            Completa tu perfil profesional para acceder a tu dashboard personalizado
          </p>
        </div>

        <div className="space-y-6">
          {/* Selección de tipo */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              ¿A qué te dedicas?
            </label>
            <div className="space-y-3">
              <div
                className={`cursor-pointer p-4 rounded-xl border-2 transition-all ${
                  selectedType === 'salud'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
                onClick={() => {
                  setSelectedType('salud');
                  setSelectedSpecialty('');
                }}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">🏥</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">Salud</h3>
                    <p className="text-sm text-gray-600">Medicina, odontología, psicología, etc.</p>
                  </div>
                </div>
              </div>

              <div
                className={`cursor-pointer p-4 rounded-xl border-2 transition-all ${
                  selectedType === 'belleza'
                    ? 'border-pink-500 bg-pink-50'
                    : 'border-gray-200 hover:border-pink-300'
                }`}
                onClick={() => {
                  setSelectedType('belleza');
                  setSelectedSpecialty('');
                }}
              >
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">💅</span>
                  <div>
                    <h3 className="font-semibold text-gray-900">Belleza</h3>
                    <p className="text-sm text-gray-600">Lashistas, manicura, peluquería, etc.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Selección de especialidad */}
          {selectedType && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Especialidad
              </label>
              <select
                value={selectedSpecialty}
                onChange={(e) => setSelectedSpecialty(e.target.value)}
                className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="">Selecciona tu especialidad</option>
                {(selectedType === 'salud' ? healthSpecialties : beautySpecialties).map((specialty) => (
                  <option key={specialty} value={specialty}>
                    {specialty}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Botón guardar */}
          <button
            onClick={handleSave}
            disabled={!selectedType || !selectedSpecialty || saving}
            className="w-full bg-gradient-to-r from-blue-500 to-green-500 text-white py-4 px-6 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {saving ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Configurando...</span>
              </>
            ) : (
              <>
                <span>Completar configuración</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Router principal con setup inicial
const ProfessionalDashboardMain: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        
        // Verificar si necesita configuración inicial
        if (parsedUser.es_profesional && !parsedUser.professional_type) {
          console.log('⚙️ Usuario necesita configuración inicial');
          setNeedsSetup(true);
        }
      } catch (error) {
        console.error('Error parsing user:', error);
      }
    }
    setLoading(false);
  }, []);

  const handleSetupComplete = () => {
    console.log('✅ Setup completado, recargando datos...');
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
      setNeedsSetup(false);
    }
  };

  if (loading) {
    return <DashboardLoader />;
  }

  if (needsSetup && user) {
    return <ProfessionalSetup user={user} onComplete={handleSetupComplete} />;
  }

  return <ProfessionalDashboardRouter />;
};

// HOC para añadir análiticas y logging
const withAnalytics = <P extends object>(Component: React.ComponentType<P>) => {
  return (props: P) => {
    useEffect(() => {
      // Registrar acceso al dashboard
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          console.log('📊 Analytics: Acceso al dashboard', {
            professional_type: user.professional_type,
            especialidad: user.especialidad,
            timestamp: new Date().toISOString()
          });
          
          // Aquí podrías enviar datos a tu servicio de analytics
          // analytics.track('dashboard_access', { ... });
        } catch (error) {
          console.error('Error en analytics:', error);
        }
      }
    }, []);

    return <Component {...props} />;
  };
};

// Exportar componente principal con analytics
export default withAnalytics(ProfessionalDashboardMain);

// También exportar los dashboards individuales para uso directo
export { 
  BeautyProfessionalDashboard, 
  HealthProfessionalDashboard, 
  ProfessionalDashboard,
  ProfessionalDashboardRouter 
};